import {useCallback, type ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {formatBytes} from '../../../common/utils/formatBytes'
import type {EstimatedGifSizeRange} from '../../../common/utils/estimateGifSize'
import {
  MAX_GIF_COLORS,
  MAX_OUTPUT_FPS,
  MAX_OUTPUT_LONG_EDGE_PX,
  MIN_GIF_COLORS,
  MIN_OUTPUT_FPS,
  MIN_OUTPUT_LONG_EDGE_PX,
} from '../../../common/constants/videoToGifLimits'

interface VideoGifSettingsProps {
  longEdgePx: number
  fps: number
  colors: number
  trimmedDurationSec: number
  /** Heuristic min/max range (see estimateGifSize.ts) — null while no video is loaded. */
  estimatedSizeRange: EstimatedGifSizeRange | null
  onLongEdgeChange: (value: number) => void
  onFpsChange: (value: number) => void
  onColorsChange: (value: number) => void
}

const VideoGifSettings = ({
  longEdgePx,
  fps,
  colors,
  trimmedDurationSec,
  estimatedSizeRange,
  onLongEdgeChange,
  onFpsChange,
  onColorsChange,
}: VideoGifSettingsProps) => {
  const {t} = useTranslation()

  const handleLongEdgeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onLongEdgeChange(Number(e.target.value)),
    [onLongEdgeChange],
  )
  const handleFpsChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onFpsChange(Number(e.target.value)),
    [onFpsChange],
  )
  const handleColorsChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onColorsChange(Number(e.target.value)),
    [onColorsChange],
  )

  return (
    <div className="vtg-settings">
      <div className="vtg-slider">
        <span className="vtg-slider-label">{t('videoToGif.settings.resolution')}</span>
        <input
          type="range"
          min={MIN_OUTPUT_LONG_EDGE_PX}
          max={MAX_OUTPUT_LONG_EDGE_PX}
          step={40}
          value={longEdgePx}
          onChange={handleLongEdgeChange}
        />
        <span className="vtg-slider-value">{longEdgePx}px</span>
      </div>
      <div className="vtg-slider">
        <span className="vtg-slider-label">{t('videoToGif.settings.fps')}</span>
        <input type="range" min={MIN_OUTPUT_FPS} max={MAX_OUTPUT_FPS} step={1} value={fps} onChange={handleFpsChange} />
        <span className="vtg-slider-value">{fps} fps</span>
      </div>
      <div className="vtg-slider">
        <span className="vtg-slider-label">{t('videoToGif.settings.quality')}</span>
        <input
          type="range"
          min={MIN_GIF_COLORS}
          max={MAX_GIF_COLORS}
          step={16}
          value={colors}
          onChange={handleColorsChange}
        />
        <span className="vtg-slider-value">{colors}</span>
      </div>
      <p className="cvt-hint">{t('videoToGif.settings.qualityHint')}</p>
      <p className="cvt-hint">{t('videoToGif.settings.durationHint', {seconds: trimmedDurationSec.toFixed(1)})}</p>
      {estimatedSizeRange !== null && (
        <p className="vtg-estimated-size">
          {t('videoToGif.settings.estimatedSize')}{' '}
          <strong>
            {formatBytes(estimatedSizeRange.minBytes)}–{formatBytes(estimatedSizeRange.maxBytes)}
          </strong>
        </p>
      )}
    </div>
  )
}

export default VideoGifSettings
